import React from "react";
import { Alert, Box, Divider, Typography } from "@mui/material";

export const NEEDS_ACTION_STATUSES = new Set([
  "NEW",
  "QUALIFYING",
  "MATCHING",
  "TOUR_SCHEDULED",
  "PENDING_DECISION",
]);

export const CLOSED_LEAD_STATUSES = new Set(["LOST", "CANCELLED", "CLOSED", "WITHDRAWN"]);
export const PENDING_INVITE_STATUSES = ["DRAFT", "SENT", "NO_RESPONSE"] as const;

export type ProviderInvite = {
  id: string;
  status: string;
  providerNote?: string | null;
  respondedAt?: string | null;
  business?: { id?: string | number | null } | null;
  group?: { id: string; seedJson?: unknown; createdAt?: string | null } | null;
};

export function parseBroadcastSeed(seedJson: unknown): {
  residentInitials?: string | null;
  careNeed?: string | null;
  payerType?: string | null;
  urgency?: string | null;
  notes?: string | null;
  referrerContact?: { name?: string; email?: string; phone?: string } | null;
} {
  try {
    const v = typeof seedJson === "string" ? JSON.parse(seedJson) : seedJson;
    if (!v || typeof v !== "object") return {};
    const anyV = v as Record<string, unknown>;
    const rawExtra = anyV.extra;
    const extra =
      rawExtra && typeof rawExtra === "string"
        ? JSON.parse(rawExtra)
        : (rawExtra as Record<string, unknown> | undefined) || {};
    return {
      residentInitials:
        (anyV.residentKey as string) ||
        (anyV.residentInitials as string) ||
        (extra.patientInitials as string) ||
        null,
      careNeed:
        (anyV.careLevelRequested as string) ||
        (anyV.careNeed as string) ||
        (extra.careNeed as string) ||
        null,
      payerType:
        (anyV.payerType as string) || (extra.payerType as string) || null,
      urgency: (anyV.urgency as string) || null,
      notes:
        (anyV.intakeNotes as string) ||
        (anyV.notes as string) ||
        (extra.notes as string) ||
        null,
      referrerContact: (extra.referrerContact as
        | { name?: string; email?: string; phone?: string }
        | null) as any,
    };
  } catch {
    return {};
  }
}

export function parseExtraLoose(raw: any) {
  if (raw == null) return {};
  if (typeof raw === "object" && !Array.isArray(raw)) return raw;
  if (typeof raw === "string") {
    try {
      const once = JSON.parse(raw);
      if (typeof once === "string") {
        try {
          const twice = JSON.parse(once);
          return twice && typeof twice === "object" && !Array.isArray(twice)
            ? twice
            : {};
        } catch {
          return {};
        }
      }
      return once && typeof once === "object" && !Array.isArray(once) ? once : {};
    } catch {
      return {};
    }
  }
  try {
    const parsed = JSON.parse(String(raw));
    return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
  } catch {
    return {};
  }
}

export function normalizePayerType(value: any): "PRIVATE" | "MEDICAID" | "MEDICARE" | "VA" | "UNKNOWN" {
  const up = String(value || "").trim().toUpperCase();
  if (up === "PRIVATE_PAY") return "PRIVATE";
  if (["PRIVATE", "MEDICAID", "MEDICARE", "VA", "UNKNOWN"].includes(up as any)) {
    return up as any;
  }
  return "UNKNOWN";
}

export function getInvitePayerType(invite: ProviderInvite) {
  return normalizePayerType(parseBroadcastSeed(invite?.group?.seedJson)?.payerType);
}

export function getReferralCardAccent(status: string) {
  switch (String(status || "").toUpperCase()) {
    case "NEW":
      return {
        border: "rgba(2, 136, 209, 0.34)",
        accent: "rgba(2, 136, 209, 0.72)",
        headerBg: "rgba(227, 242, 253, 0.82)",
        bodyBg: "rgba(255,255,255,0.99)",
      };
    case "PLACED":
    case "SETTLED":
      return {
        border: "rgba(46, 125, 50, 0.28)",
        accent: "rgba(46, 125, 50, 0.66)",
        headerBg: "rgba(232, 245, 233, 0.86)",
        bodyBg: "rgba(255,255,255,0.99)",
      };
    case "LOST":
    case "CANCELLED":
    case "CLOSED":
    case "WITHDRAWN":
      return {
        border: "rgba(117, 117, 117, 0.28)",
        accent: "rgba(117, 117, 117, 0.55)",
        headerBg: "rgba(245, 245, 245, 0.9)",
        bodyBg: "rgba(252,252,252,0.98)",
      };
    default:
      return {
        border: "rgba(103, 114, 126, 0.24)",
        accent: "rgba(21, 49, 76, 0.52)",
        headerBg: "rgba(248, 250, 252, 0.96)",
        bodyBg: "rgba(255,255,255,0.99)",
      };
  }
}

export function deriveAgentFromExtra(extra: any) {
  const a =
    extra?.agent || extra?.concierge?.agent || extra?.assignedAgent || null;

  if (!a) return null;

  if (typeof a === "string") {
    try {
      const o = JSON.parse(a);
      return {
        name: o?.name ?? null,
        email: o?.email ?? null,
        phone: o?.phone ?? null,
      };
    } catch {
      return { name: a, email: null, phone: null };
    }
  }

  return {
    name: a?.name ?? null,
    email: a?.email ?? null,
    phone: a?.phone ?? null,
  };
}

export function getTourScheduleFromLead(lead: any): {
  bookingTime?: string | null;
  businessName?: string | null;
} | null {
  const status = String(lead?.status || "").toUpperCase();
  const extra = parseExtraLoose(lead?.extra);
  const tourSchedule = extra?.tourSchedule;
  const bookingTime = String(tourSchedule?.bookingTime || "").trim();
  if (!bookingTime && status !== "TOUR_SCHEDULED") return null;
  return {
    bookingTime: bookingTime || null,
    businessName:
      String(tourSchedule?.businessName || lead?.business?.name || "").trim() ||
      null,
  };
}

function parseTourScheduleDate(bookingTime?: string | null): Date | null {
  if (!bookingTime) return null;
  const trimmed = String(bookingTime).trim();
  if (!trimmed) return null;
  const numeric = Number(trimmed);
  const dt =
    !Number.isNaN(numeric)
      ? new Date(numeric > 1e12 ? numeric : numeric * 1000)
      : new Date(trimmed);
  return Number.isNaN(dt.getTime()) ? null : dt;
}

export function formatTourScheduleLabel(bookingTime?: string | null): string | null {
  const dt = parseTourScheduleDate(bookingTime);
  if (!dt) return null;
  return dt.toLocaleString(undefined, {
    weekday: "long",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).replace(/\s?(AM|PM)\b/g, (m) => m.toLowerCase());
}

export function findScheduledTourBookingForLead(
  lead: any,
  bookings: any[],
  fallbackBusinessName?: string | null,
): {
  bookingTime?: string | null;
  businessName?: string | null;
} | null {
  if (!Array.isArray(bookings) || bookings.length === 0) return null;
  const leadId = String(lead?.id || "").trim();
  const extra = parseExtraLoose(lead?.extra);
  const scheduledBookingId = String(extra?.tourSchedule?.bookingId || "").trim();
  const conciergeMirrorBookingId = String(
    extra?.tourSchedule?.conciergeMirrorBookingId || ""
  ).trim();
  const providerName =
    String(
      extra?.tourSchedule?.businessName || lead?.business?.name || fallbackBusinessName || ""
    ).trim() || null;
  if (!leadId && !scheduledBookingId && !conciergeMirrorBookingId) return null;
  const now = Date.now();
  const referrerEmail = String(
    lead?.referrer?.email ||
      lead?.referrerContact?.email ||
      extra?.referrerContact?.email ||
      ""
  )
    .trim()
    .toLowerCase();
  const referrerName = String(
    lead?.referrer?.contactName ||
      lead?.referrerContact?.name ||
      extra?.referrerContact?.name ||
      ""
  )
    .trim()
    .toLowerCase();
  const referrerPhoneDigits = String(
    lead?.referrer?.phone ||
      lead?.referrerContact?.phone ||
      extra?.referrerContact?.phone ||
      ""
  ).replace(/\D/g, "");
  const activeBookings = bookings.filter((booking: any) => {
    if (Number(booking?.status) === 0) return false;
    const when = parseTourScheduleDate(String(booking?.time || "").trim())?.getTime();
    if (when == null || !Number.isFinite(when)) return false;
    return when >= now - 24 * 60 * 60 * 1000;
  });
  const matchesStoredBooking = (booking: any) => {
    const bookingId = String(booking?.id || "").trim();
    const externalBookingId = String(booking?.bookingId || "").trim();
    return (
      (!!scheduledBookingId && bookingId === scheduledBookingId) ||
      (!!conciergeMirrorBookingId && bookingId === conciergeMirrorBookingId) ||
      (!!scheduledBookingId && externalBookingId === `coord:${scheduledBookingId}`)
    );
  };
  const storedMatch =
    scheduledBookingId || conciergeMirrorBookingId
      ? activeBookings.find((booking: any) => matchesStoredBooking(booking)) || null
      : null;
  const leadMatch = activeBookings.find((booking: any) => {
    const referralLeadId = String(booking?.referralLeadId || "").trim();
    if (referralLeadId && referralLeadId === leadId) return true;
    const notes = String(booking?.quickNotes || "").trim();
    const leadMatch = notes.match(/(?:referral|tour\s+for)\s+lead\s*#(\d+)/i);
    return !!leadMatch?.[1] && String(leadMatch[1]).trim() === leadId;
  });
  const exactMatch = storedMatch || leadMatch || null;
  const identityMatch = exactMatch
    ? null
    : activeBookings.find((booking: any) => {
        const emails = [
          booking?.inviteeEmail,
          booking?.bookedByEmail,
          booking?.bookedForEmail,
          ...(Array.isArray(booking?.confirmationEmails) ? booking.confirmationEmails : []),
        ]
          .map((value: any) => String(value || "").trim().toLowerCase())
          .filter(Boolean);
        const names = [
          booking?.inviteeName,
          booking?.bookedByName,
          booking?.bookedForName,
        ]
          .map((value: any) => String(value || "").trim().toLowerCase())
          .filter(Boolean);
        const phones = [
          booking?.bookedForPhone,
          ...(Array.isArray(booking?.inviteePhoneNumbers) ? booking.inviteePhoneNumbers : []),
        ]
          .map((value: any) => String(value || "").replace(/\D/g, ""))
          .filter(Boolean);

        const emailMatches = !!referrerEmail && emails.includes(referrerEmail);
        const nameMatches = !!referrerName && names.includes(referrerName);
        const phoneMatches = !!referrerPhoneDigits && phones.includes(referrerPhoneDigits);

        return emailMatches || (nameMatches && phoneMatches) || nameMatches;
      });
  const match = exactMatch || identityMatch;
  if (!match) return null;
  return {
    bookingTime: String(match?.time || "").trim() || null,
    businessName: providerName,
  };
}

export function getResolvedBookedTour(
  lead: any,
  bookings: any[],
  fallbackBusinessName?: string | null,
): {
  bookingTime?: string | null;
  businessName?: string | null;
} | null {
  const activeBooking = findScheduledTourBookingForLead(lead, bookings, fallbackBusinessName);
  if (activeBooking) return activeBooking;
  const storedSchedule = getTourScheduleFromLead(lead);
  if (storedSchedule) {
    return {
      bookingTime: storedSchedule.bookingTime || null,
      businessName: storedSchedule.businessName || fallbackBusinessName || null,
    };
  }
  return null;
}

export function getLatestTourCancellation(lead: any): {
  canceledAt?: string | null;
  canceledByName?: string | null;
  canceledByRole?: string | null;
  businessName?: string | null;
  bookingTime?: string | null;
} | null {
  const extra = parseExtraLoose(lead?.extra);
  const cancellation = extra?.lastTourCancellation;
  if (!cancellation) return null;
  return {
    canceledAt: String(cancellation?.canceledAt || "").trim() || null,
    canceledByName: String(cancellation?.canceledByName || "").trim() || null,
    canceledByRole: String(cancellation?.canceledByRole || "").trim() || null,
    businessName:
      String(cancellation?.businessName || lead?.business?.name || "").trim() || null,
    bookingTime: String(cancellation?.bookingTime || "").trim() || null,
  };
}

export function ConciergeBannerForProvider({
  isRush,
  contactUnlocked,
  agent,
  refName,
  bookedTour,
}: {
  isRush: boolean;
  contactUnlocked: boolean;
  agent: {
    name?: string | null;
    email?: string | null;
    phone?: string | null;
  } | null;
  refName?: string | null;
  bookedTour?: {
    bookingTime?: string | null;
    businessName?: string | null;
  } | null;
}) {
  const bookedLabel = formatTourScheduleLabel(bookedTour?.bookingTime);
  return (
    <Alert severity={bookedTour ? "success" : "info"} sx={{ mt: 1 }}>
      <Typography variant="subtitle2" fontWeight={700}>
        Concierge referral {isRush ? "(Rush)" : ""}
      </Typography>

      {bookedTour ? (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Tour already scheduled
          {bookedLabel ? ` for ${bookedLabel}` : ""}
          {refName ? ` with ${refName}` : ""}. Our specialist will continue follow-up and next-step coordination.
        </Typography>
      ) : !contactUnlocked ? (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          Our placement specialist will coordinate with{" "}
          {refName ? <b>{refName}</b> : "the referrer"} and your team to move
          things forward.
          {isRush
            ? " We’ll begin outreach immediately and prioritize same-day next steps."
            : " We’ll get back to you soon.."}
        </Typography>
      ) : (
        <Typography variant="body2" sx={{ mt: 0.5 }}>
          You’ve shared contact with the referrer. Our specialist will help
          confirm fit, arrange a tour, and align on pricing and move-in details.
        </Typography>
      )}

      <Box component="ul" sx={{ mt: 1, pl: 3, mb: 0 }}>
        <li>
          <Typography variant="body2">
            Confirm availability and care fit.
          </Typography>
        </li>
        <li>
          <Typography variant="body2">
            {bookedTour
              ? "Tour has been booked. Confirm visit logistics and follow up after the tour."
              : "Schedule a tour (virtual or in-person) as needed."}
          </Typography>
        </li>
      </Box>

      {!!agent && (agent.name || agent.email || agent.phone) && (
        <>
          <Divider sx={{ my: 1 }} />
          <Typography variant="body2">
            <em>Placement specialist</em>:{" "}
            {agent.name ? <b>{agent.name}</b> : "Assigned"}
            {(agent.email || agent.phone) && " — "}
            {agent.email && <a href={`mailto:${agent.email}`}>{agent.email}</a>}
            {agent.email && agent.phone ? " • " : ""}
            {agent.phone && <a href={`tel:${agent.phone}`}>{agent.phone}</a>}
          </Typography>
        </>
      )}
    </Alert>
  );
}
